import React, {useEffect, useState} from 'react'
import {
  fetchStanjeNaloga,
  fetchStanjeNalogaGodina,
  fetchStanjeNalogaKomercijala,
} from '../../../api/proizvodnja'
import SimpleBarChart from './SimpleBarChart'

type Props = {
  korisnik: number
  suma: number
  otpremiti: number
  cijena: number
  canViewCijena?: boolean
  zavrseniNalozi: number
  isporucenoZadnjaSedmica: number
}

type Metrics = {
  BrojKreiranihZK?: number
  BrojRealizovanihPN?: number
  VrijednostKreiranihZK?: number
  VrijednostrealizovanihPN?: number
}
type AnnualData = Metrics & {
  Godina: number
  Godina0: number
  BrojKreiranihZK0?: number
  BrojRealizovanihPN0?: number
  VrijednostKreiranihZK0?: number
  VrijednostrealizovanihPN0?: number
}
type MonthData = Metrics & {Mjesec: number}
type MonthlyData = {
  trenutna_godina: {godina: number; dataMjeseciKomercijalista: MonthData[]}
  prosla_godina: {godina: number; dataMjeseciKomercijalista: MonthData[]}
}

const formatter = new Intl.NumberFormat('bs-BA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const number = (value?: number) => (Number.isFinite(Number(value)) ? Number(value) : 0)
const percent = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0)
const calc = (entry: Metrics) => ({
  percentage: percent(number(entry.BrojRealizovanihPN), number(entry.BrojKreiranihZK)).toFixed(1),
  difference: number(entry.VrijednostKreiranihZK) - number(entry.VrijednostrealizovanihPN),
  valuePercentage: percent(
    number(entry.VrijednostrealizovanihPN),
    number(entry.VrijednostKreiranihZK)
  ).toFixed(1),
})

const Section = ({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) => (
  <section className='card shadow-sm'>
    <div className='card-header border-bottom py-5'>
      <div>
        <h3 className='fs-4 fw-bold mb-1'>{title}</h3>
        {subtitle && <p className='text-muted fs-7 mb-0'>{subtitle}</p>}
      </div>
    </div>
    <div className='card-body p-5 p-lg-7'>{children}</div>
  </section>
)

const MetricCard = ({
  label,
  value,
  unit = 'm²',
  tone,
  icon,
  note,
}: {
  label: string
  value: number
  unit?: string
  tone: string
  icon: string
  note: string
}) => (
  <div className={`card shadow-sm h-100 border-top border-3 border-${tone}`}>
    <div className='card-body p-5'>
      <div className='d-flex align-items-center gap-3 mb-4'>
        <span className={`bg-light-${tone} rounded p-3`}>
          <i className={`bi ${icon} text-${tone} fs-3`} aria-hidden='true' />
        </span>
        <div className='fw-semibold text-gray-700'>{label}</div>
      </div>
      <div className='fs-2 fw-bold text-gray-900 text-break mb-2'>
        {formatter.format(value)} <span className='fs-6 fw-normal text-muted'>{unit}</span>
      </div>
      <div className='text-muted fs-7'>{note}</div>
    </div>
  </div>
)

const PeriodSummary = ({
  title,
  finished,
  shipped,
}: {
  title: string
  finished: number
  shipped: number
}) => (
  <Section title={title}>
    <dl className='mb-0'>
      {[
        {label: 'Završeni nalozi', value: finished, tone: 'primary'},
        {label: 'Otpremljeno', value: shipped, tone: 'success'},
        {label: 'Za otpremiti', value: finished - shipped, tone: 'danger'},
      ].map(({label, value, tone}, i) => (
        <div
          key={label}
          className={`d-flex flex-wrap align-items-center justify-content-between gap-3 py-4 ${
            i ? 'border-top' : ''
          }`}
        >
          <dt className='text-gray-700 fw-normal'>{label}</dt>
          <dd className={`fw-bold fs-5 text-${tone} mb-0`}>
            {formatter.format(value)} <span className='fs-7'>m²</span>
          </dd>
        </div>
      ))}
    </dl>
  </Section>
)

const AnnualComparison = ({title, entry}: {title: string; entry: AnnualData}) => {
  const previous = {
    BrojKreiranihZK: entry.BrojKreiranihZK0,
    BrojRealizovanihPN: entry.BrojRealizovanihPN0,
    VrijednostKreiranihZK: entry.VrijednostKreiranihZK0,
    VrijednostrealizovanihPN: entry.VrijednostrealizovanihPN0,
  }
  const currentCalc = calc(entry)
  const previousCalc = calc(previous)
  const rows = [
    ['Otvorene ponude', number(entry.BrojKreiranihZK), number(previous.BrojKreiranihZK)],
    ['Realizovane ponude', number(entry.BrojRealizovanihPN), number(previous.BrojRealizovanihPN)],
    ['Realizacija po broju', `${currentCalc.percentage}%`, `${previousCalc.percentage}%`],
    [
      'Vrijednost otvorenih ponuda',
      `${formatter.format(number(entry.VrijednostKreiranihZK))} KM`,
      `${formatter.format(number(previous.VrijednostKreiranihZK))} KM`,
    ],
    [
      'Vrijednost realizovanih ponuda',
      `${formatter.format(number(entry.VrijednostrealizovanihPN))} KM`,
      `${formatter.format(number(previous.VrijednostrealizovanihPN))} KM`,
    ],
    [
      'Razlika',
      `${formatter.format(currentCalc.difference)} KM`,
      `${formatter.format(previousCalc.difference)} KM`,
    ],
    [
      'Realizacija po vrijednosti',
      `${currentCalc.valuePercentage}%`,
      `${previousCalc.valuePercentage}%`,
    ],
  ]
  const charts = [
    {
      title: 'Broj ponuda',
      currency: false,
      data: [
        {
          name: 'Otvorene ponude',
          current: number(entry.BrojKreiranihZK),
          previous: number(previous.BrojKreiranihZK),
        },
        {
          name: 'Realizovane ponude',
          current: number(entry.BrojRealizovanihPN),
          previous: number(previous.BrojRealizovanihPN),
        },
      ],
    },
    {
      title: 'Vrijednost ponuda',
      currency: true,
      data: [
        {
          name: 'Otvorene ponude',
          current: number(entry.VrijednostKreiranihZK),
          previous: number(previous.VrijednostKreiranihZK),
        },
        {
          name: 'Realizovane ponude',
          current: number(entry.VrijednostrealizovanihPN),
          previous: number(previous.VrijednostrealizovanihPN),
        },
      ],
    },
  ]
  return (
    <Section
      title={title}
      subtitle={`Poređenje rezultata za ${entry.Godina}. i ${entry.Godina0}. godinu.`}
    >
      <div className='row g-6 mb-7'>
        {charts.map((chart) => (
          <div key={chart.title} className='col-12 col-lg-6'>
            <div className='border rounded p-5 h-100'>
              <h4 className='fs-5 fw-bold mb-5'>
                {chart.title}
                {chart.currency ? ' (KM)' : ''}
              </h4>
              <SimpleBarChart
                data={chart.data}
                year1={entry.Godina}
                year2={entry.Godina0}
                isCurrency={chart.currency}
              />
            </div>
          </div>
        ))}
      </div>
      <div className='table-responsive'>
        <table className='table table-row-dashed align-middle gy-4 gs-4 mb-0'>
          <caption className='visually-hidden'>{title} - detaljno poređenje</caption>
          <thead className='bg-light'>
            <tr>
              <th scope='col'>Stavka</th>
              <th scope='col' className='text-end text-nowrap'>
                {entry.Godina}. godina
              </th>
              <th scope='col' className='text-end text-nowrap'>
                {entry.Godina0}. godina
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, current, past]) => (
              <tr key={label}>
                <th scope='row' className='fw-normal text-gray-700'>
                  {label}
                </th>
                <td className='text-end fw-bold text-nowrap'>{current}</td>
                <td className='text-end text-nowrap'>{past}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  )
}

const MonthlyComparison = ({data}: {data: MonthlyData}) => {
  const current = data.trenutna_godina
  const previous = data.prosla_godina
  const currentRows = current.dataMjeseciKomercijalista ?? []
  const previousRows = previous.dataMjeseciKomercijalista ?? []
  const months = Array.from(
    new Set([...currentRows, ...previousRows].map((row) => Number(row.Mjesec)))
  ).sort((a, b) => a - b)
  const names = [
    'Januar',
    'Februar',
    'Mart',
    'April',
    'Maj',
    'Juni',
    'Juli',
    'August',
    'Septembar',
    'Oktobar',
    'Novembar',
    'Decembar',
  ]
  return (
    <Section
      title='Zadnja tri mjeseca'
      subtitle='Mjesečni pregled otvorenih i realizovanih ponuda za obje godine.'
    >
      <div className='table-responsive'>
        <table className='table table-row-dashed align-middle gy-4 gs-3 mb-0'>
          <caption className='visually-hidden'>Poređenje za zadnja tri mjeseca</caption>
          <thead className='bg-light'>
            <tr>
              {[
                'Mjesec / godina',
                'Otvorene',
                'Realizovane',
                'Realizacija',
                'Vrijednost otvorenih (KM)',
                'Vrijednost realizovanih (KM)',
              ].map((label) => (
                <th scope='col' key={label}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {months.map((month) => (
              <React.Fragment key={month}>
                {[
                  {
                    year: current.godina,
                    entry: currentRows.find((row) => Number(row.Mjesec) === month),
                  },
                  {
                    year: previous.godina,
                    entry: previousRows.find((row) => Number(row.Mjesec) === month),
                  },
                ].map(({year, entry = {}}) => (
                  <tr key={year}>
                    <th scope='row' className='text-nowrap fw-semibold'>
                      {names[month - 1] || month} <span className='text-muted ms-2'>{year}.</span>
                    </th>
                    <td className='text-end'>{number(entry.BrojKreiranihZK)}</td>
                    <td className='text-end'>{number(entry.BrojRealizovanihPN)}</td>
                    <td className='text-end fw-semibold'>{calc(entry).percentage}%</td>
                    <td className='text-end text-nowrap'>
                      {formatter.format(number(entry.VrijednostKreiranihZK))}
                    </td>
                    <td className='text-end text-nowrap fw-semibold'>
                      {formatter.format(number(entry.VrijednostrealizovanihPN))}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {!months.length && (
          <div className='text-center text-muted py-5'>Nema podataka za ovaj period.</div>
        )}
      </div>
    </Section>
  )
}

const RealizacijaReport: React.FC<Props> = ({
  korisnik,
  suma,
  otpremiti,
  cijena,
  canViewCijena = false,
  zavrseniNalozi,
  isporucenoZadnjaSedmica,
}) => {
  const [data3m, setData3m] = useState<MonthlyData | null>(null)
  const [dataYear, setDataYear] = useState<AnnualData | null>(null)
  const [dataAll, setDataAll] = useState<AnnualData | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let active = true
    setData3m(null)
    setDataYear(null)
    setDataAll(null)
    setFailed(false)
    if (!korisnik) {
      setLoading(false)
      return
    }
    setLoading(true)
    Promise.allSettled([
      fetchStanjeNaloga(String(korisnik)),
      fetchStanjeNalogaGodina(String(korisnik)),
      fetchStanjeNalogaKomercijala(String(korisnik)),
    ]).then(([monthly, yearly, all]) => {
      if (!active) return
      if (monthly.status === 'fulfilled')
        setData3m((monthly.value as unknown as {data?: MonthlyData})?.data ?? null)
      if (yearly.status === 'fulfilled')
        setDataYear(
          ((Array.isArray(yearly.value)
            ? yearly.value[0]
            : yearly.value) as unknown as AnnualData) ?? null
        )
      if (all.status === 'fulfilled')
        setDataAll(
          ((Array.isArray(all.value) ? all.value[0] : all.value) as unknown as AnnualData) ?? null
        )
      setFailed([monthly, yearly, all].some((result) => result.status === 'rejected'))
      setLoading(false)
    })
    return () => {
      active = false
    }
  }, [korisnik, retry])

  return (
    <div className='d-flex flex-column gap-7'>
      <section aria-label='Ukupno stanje završenih naloga'>
        <div className='mb-5'>
          <h3 className='fs-4 fw-bold mb-1'>Ukupno stanje završenih naloga</h3>
          <p className='text-muted fs-7 mb-0'>
            Zbir prikazanih redova prema trenutnim filterima i stranici liste.
          </p>
        </div>
        <div className='row g-4'>
          <div className={`col-12 col-sm-6 ${canViewCijena ? 'col-xl-3' : 'col-xl-4'}`}>
            <MetricCard
              label='Ukupno završeno'
              value={suma}
              tone='primary'
              icon='bi-layers'
              note='Ukupna površina panela'
            />
          </div>
          <div className={`col-12 col-sm-6 ${canViewCijena ? 'col-xl-3' : 'col-xl-4'}`}>
            <MetricCard
              label='Otpremljeno'
              value={suma - otpremiti}
              tone='success'
              icon='bi-truck'
              note={`${percent(suma - otpremiti, suma).toFixed(1)}% ukupne površine`}
            />
          </div>
          <div className={`col-12 col-sm-6 ${canViewCijena ? 'col-xl-3' : 'col-xl-4'}`}>
            <MetricCard
              label='Za otpremiti'
              value={otpremiti}
              tone='danger'
              icon='bi-box-seam'
              note={`${percent(otpremiti, suma).toFixed(1)}% ukupne površine`}
            />
          </div>
          {canViewCijena && (
            <div className='col-12 col-sm-6 col-xl-3'>
              <MetricCard
                label='Cijena (KM)'
                value={cijena}
                unit='KM'
                tone='warning'
                icon='bi-cash-stack'
                note='Zbir kolone Cijena (m2)'
              />
            </div>
          )}
        </div>
      </section>
      <div className='row g-6'>
        <div className='col-12 col-lg-6'>
          <PeriodSummary
            title='Zadnjih 7 dana'
            finished={zavrseniNalozi}
            shipped={isporucenoZadnjaSedmica}
          />
        </div>
        <div className='col-12 col-lg-6'>
          <PeriodSummary
            title='Starije od 7 dana'
            finished={suma - zavrseniNalozi}
            shipped={suma - otpremiti - isporucenoZadnjaSedmica}
          />
        </div>
      </div>
      {!korisnik ? (
        <div className='alert alert-primary mb-0'>
          Za godišnje i mjesečno poređenje odaberite korisnika u filterima liste.
        </div>
      ) : (
        <>
          {loading && (
            <div className='card p-6 text-center' role='status'>
              <span>Učitavanje poređenja…</span>
            </div>
          )}
          {failed && (
            <div
              className='alert alert-warning d-flex flex-wrap justify-content-between align-items-center gap-3 mb-0'
              role='alert'
            >
              <span>Dio podataka nije učitan. Dostupni rezultati su prikazani.</span>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() => setRetry((value) => value + 1)}
              >
                Pokušaj ponovo
              </button>
            </div>
          )}
          {dataYear && (
            <AnnualComparison
              title={`Godišnja realizacija · korisnik ${korisnik}`}
              entry={dataYear}
            />
          )}
          {data3m && <MonthlyComparison data={data3m} />}
          {dataAll && <AnnualComparison title='Svi komercijalisti' entry={dataAll} />}
          {!loading && !failed && !dataYear && !data3m && !dataAll && (
            <div className='text-muted text-center py-5'>Nema podataka za odabranog korisnika.</div>
          )}
        </>
      )}
    </div>
  )
}

export default RealizacijaReport
